/**
 * Drag-and-drop runtime. Mouse and pen drags run on Pragmatic drag and drop (Apache-2.0, native HTML5
 * drag events: autoscroll, files, other windows). Touch drags run on pointer events from a drag handle
 * (native HTML5 drag is unreliable on touch screens), and keyboard drags are driven by the list and tree
 * hooks. All three feed the same drop targets with the same typed payloads and points.
 */
import {
  draggable as pdndDraggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { getLayerStack } from '@dataroom/ui';
import type { DragPayload, Point } from './model';

const PAYLOAD_KEY = '__drPayload';

/** A drop target as the touch path sees it (the pointer path goes through Pragmatic). */
export type DropTargetHandlers = {
  element: Element;
  accepts: (payload: DragPayload) => boolean;
  onDrag?: (payload: DragPayload, point: Point) => void;
  onLeave?: () => void;
  onDrop: (payload: DragPayload, point: Point) => void;
};

export type DragSession = {
  payload: DragPayload;
  mode: 'pointer' | 'touch' | 'keyboard';
  source: Element | null;
};

const touchTargets = new Set<DropTargetHandlers>();
const sessionListeners = new Set<(session: DragSession | null) => void>();
let session: DragSession | null = null;

function setSession(next: DragSession | null): void {
  session = next;
  for (const l of [...sessionListeners]) l(session);
}

/** The drag in progress (any mode). */
export function currentDrag(): DragSession | null {
  return session;
}

export function subscribeDrag(listener: (session: DragSession | null) => void): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

/** Mark a keyboard drag (so other surfaces can show where it may go). */
export function setKeyboardDrag(payload: DragPayload | null, source: Element | null = null): void {
  setSession(payload ? { payload, mode: 'keyboard', source } : null);
}

const payloadOf = (data: Record<string | symbol, unknown>): DragPayload | null =>
  (data[PAYLOAD_KEY] as DragPayload | undefined) ?? null;

// -- Live announcements ----------------------------------------------------------------------------

let announcer: HTMLElement | null = null;

/** Speak a message politely to screen readers (drag and drop, reorder results). */
export function announce(message: string): void {
  if (typeof document === 'undefined') return;
  if (!announcer || !announcer.isConnected) {
    announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'assertive');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.setAttribute('role', 'status');
    announcer.className = 'dr-sr-only';
    announcer.dataset.drAnnouncer = '';
    const host = getLayerStack().container('system') ?? document.body;
    host.appendChild(announcer);
  }
  // Clear first so the same message is announced again.
  announcer.textContent = '';
  const el = announcer;
  setTimeout(() => {
    el.textContent = message;
  }, 30);
}

/** Last announcement (tests and the dev gallery). */
export function lastAnnouncement(): string {
  return announcer?.textContent ?? '';
}

// -- Draggables ------------------------------------------------------------------------------------

export type DraggableOptions = {
  element: HTMLElement;
  /** The grip; touch drags start only here (so a finger on the row still scrolls). Defaults to the element. */
  handle?: HTMLElement | null;
  /**
   * Where a mouse or pen drag may start: anywhere on the element (default, a bigger target) or only on
   * the handle (rows with selectable text or their own pointer gestures).
   */
  pointerFrom?: 'element' | 'handle';
  /**
   * How a finger starts a drag: from the handle at once (default), or by pressing and holding the
   * element (`long-press`, for rows without a grip such as the ui `Tree`: a swipe still scrolls).
   */
  touch?: 'handle' | 'long-press';
  payload: () => DragPayload;
  /**
   * Native data for other tabs, windows and apps (`{ mime: value }`), e.g. `nativeDragData(item)`;
   * pointer drags only. In-app targets still receive `payload`.
   */
  external?: () => Record<string, string>;
  canDrag?: () => boolean;
  onStart?: (mode: 'pointer' | 'touch') => void;
  onEnd?: (dropped: boolean) => void;
};

let monitoring = false;

/**
 * End the pointer session on every drop, even when the source's draggable was removed mid-drag (a
 * virtualised row scrolled out of view does not receive its own `onDrop`).
 */
function ensureMonitor(): void {
  if (monitoring || typeof window === 'undefined') return;
  monitoring = true;
  monitorForElements({
    onDrop: () => {
      if (session?.mode === 'pointer') setSession(null);
    },
  });
}

/** Make an element draggable by mouse, pen and touch. Returns the cleanup. */
export function makeDraggable(options: DraggableOptions): () => void {
  ensureMonitor();
  const handle = options.handle ?? options.element;
  const cleanups: Array<() => void> = [];
  cleanups.push(
    pdndDraggable({
      element: options.element,
      ...(options.handle && options.pointerFrom === 'handle' ? { dragHandle: options.handle } : {}),
      canDrag: () => !touch.active && (options.canDrag?.() ?? true),
      getInitialData: () => ({ [PAYLOAD_KEY]: options.payload() }),
      ...(options.external ? { getInitialDataForExternal: options.external } : {}),
      onDragStart: ({ source }) => {
        setSession({ payload: payloadOf(source.data)!, mode: 'pointer', source: options.element });
        options.onStart?.('pointer');
      },
      onDrop: ({ location }) => {
        setSession(null);
        options.onEnd?.(location.current.dropTargets.length > 0);
      },
    }),
  );
  const longPress = options.touch === 'long-press';
  const onPointerDown = (event: PointerEvent) => {
    if (event.pointerType !== 'touch' || !(options.canDrag?.() ?? true)) return;
    startTouch(event, options, handle, longPress);
  };
  handle.addEventListener('pointerdown', onPointerDown);
  cleanups.push(() => handle.removeEventListener('pointerdown', onPointerDown));
  if (longPress) {
    // A blocking touchmove listener must exist when the finger lands (Chrome decides at touchstart
    // whether a touch may scroll); it cancels scrolling only while a held drag is running.
    const onTouchMove = (event: TouchEvent) => {
      if (touch.active && event.cancelable) event.preventDefault();
    };
    handle.addEventListener('touchmove', onTouchMove, { passive: false });
    cleanups.push(() => handle.removeEventListener('touchmove', onTouchMove));
  }
  return () => {
    for (const c of cleanups) c();
  };
}

// -- Drop targets ----------------------------------------------------------------------------------

/**
 * Pragmatic allows one drop target per element. Two hooks may both want the same element (a tree row
 * that reorders tree items and also takes items dragged from a list): they share one registration
 * and each handler answers the payloads it accepts.
 */
const sharedTargets = new Map<Element, { handlers: Set<DropTargetHandlers>; cleanup: () => void }>();

export function makeDropTarget(handlers: DropTargetHandlers): () => void {
  touchTargets.add(handlers);
  const { element } = handlers;
  let group = sharedTargets.get(element);
  if (!group) {
    const set = new Set<DropTargetHandlers>();
    const point = (input: { clientX: number; clientY: number }): Point => ({
      x: input.clientX,
      y: input.clientY,
    });
    const accepting = (payload: DragPayload) => [...set].filter((h) => h.accepts(payload));
    const cleanup = dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        const payload = payloadOf(source.data);
        return Boolean(payload && accepting(payload).length);
      },
      onDragEnter: ({ source, location }) => {
        const payload = payloadOf(source.data)!;
        for (const h of accepting(payload)) h.onDrag?.(payload, point(location.current.input));
      },
      onDrag: ({ source, location }) => {
        const payload = payloadOf(source.data)!;
        for (const h of accepting(payload)) h.onDrag?.(payload, point(location.current.input));
      },
      onDragLeave: () => {
        for (const h of set) h.onLeave?.();
      },
      onDrop: ({ source, location }) => {
        // Only the innermost target handles the drop; outer ones just clear their feedback.
        const payload = payloadOf(source.data)!;
        if (location.current.dropTargets[0]?.element === element)
          accepting(payload)[0]?.onDrop(payload, point(location.current.input));
        else for (const h of set) h.onLeave?.();
      },
    });
    group = { handlers: set, cleanup };
    sharedTargets.set(element, group);
  }
  group.handlers.add(handlers);
  const mine = group;
  return () => {
    touchTargets.delete(handlers);
    mine.handlers.delete(handlers);
    if (!mine.handlers.size) {
      mine.cleanup();
      if (sharedTargets.get(element) === mine) sharedTargets.delete(element);
    }
  };
}

const autoScrolls = new WeakMap<Element, { users: number; cleanup: () => void }>();

/**
 * Autoscroll a scroll container while something is dragged over it (pointer path). One scroller per
 * element however many lists sit in it (Pragmatic warns about a second one): users are counted and
 * the scroller goes with the last of them.
 */
export function makeAutoScroll(element: Element): () => void {
  let entry = autoScrolls.get(element);
  if (!entry) {
    entry = { users: 0, cleanup: autoScrollForElements({ element }) };
    autoScrolls.set(element, entry);
  }
  const mine = entry;
  mine.users += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    mine.users -= 1;
    if (mine.users <= 0) {
      mine.cleanup();
      if (autoScrolls.get(element) === mine) autoScrolls.delete(element);
    }
  };
}

/** The nearest scrollable ancestor (or the element itself). */
export function scrollParent(element: Element | null): HTMLElement | null {
  for (let el = element instanceof HTMLElement ? element : null; el; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (/(auto|scroll)/.test(`${style.overflowY} ${style.overflowX}`) && el.scrollHeight > el.clientHeight)
      return el;
  }
  return null;
}

// -- Touch path ------------------------------------------------------------------------------------

const TOUCH_SLOP = 4;
/** Movement that turns a pending press-and-hold into a scroll (no drag). */
const HOLD_SLOP = 8;
/** Press-and-hold time before a `long-press` drag starts (shorter than the OS context menu). */
export const LONG_PRESS_MS = 350;
const EDGE = 48;

const touch: {
  active: boolean;
  target: DropTargetHandlers | null;
  preview: HTMLElement | null;
  raf: number;
  point: Point;
} = { active: false, target: null, preview: null, raf: 0, point: { x: 0, y: 0 } };

function innermostTarget(hit: Element | null, payload: DragPayload): DropTargetHandlers | null {
  let best: DropTargetHandlers | null = null;
  for (const t of touchTargets) {
    if (!hit || !t.element.contains(hit) || !t.accepts(payload)) continue;
    if (!best || best.element.contains(t.element)) best = t;
  }
  return best;
}

/**
 * A finger drag. From a handle it starts after a few pixels of movement; with `hold` it starts after
 * the finger rests for `LONG_PRESS_MS` (moving earlier lets the page scroll as usual). Move and up
 * events are read on the window, so a virtualised row that unmounts mid-drag does not end it.
 */
function startTouch(down: PointerEvent, options: DraggableOptions, handle: HTMLElement, hold: boolean): void {
  const start: Point = { x: down.clientX, y: down.clientY };
  let payload: DragPayload | null = null;
  let offset: Point = { x: 0, y: 0 };
  let timer = 0;
  if (!hold) {
    try {
      handle.setPointerCapture(down.pointerId);
    } catch {
      /* synthetic events */
    }
    down.preventDefault();
  }
  touch.point = start;

  const begin = () => {
    payload = options.payload();
    touch.active = true;
    setSession({ payload, mode: 'touch', source: options.element });
    const rect = options.element.getBoundingClientRect();
    offset = { x: start.x - rect.left, y: start.y - rect.top };
    const preview = options.element.cloneNode(true) as HTMLElement;
    preview.removeAttribute('id');
    preview.setAttribute('aria-hidden', 'true');
    preview.classList.add('dr-drag-preview');
    // Rows of virtual lists carry their own position; the preview follows the finger instead.
    preview.style.position = 'fixed';
    preview.style.left = '0';
    preview.style.top = '0';
    preview.style.width = `${rect.width}px`;
    preview.style.height = `${rect.height}px`;
    preview.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
    const host = getLayerStack().container('layers') ?? document.body;
    host.appendChild(preview);
    touch.preview = preview;
    options.element.setAttribute('data-dragging', '');
    options.onStart?.('touch');
    const tick = () => {
      autoscrollAt(touch.point);
      touch.raf = requestAnimationFrame(tick);
    };
    touch.raf = requestAnimationFrame(tick);
  };

  const track = (point: Point) => {
    if (touch.preview)
      touch.preview.style.transform = `translate(${point.x - offset.x}px, ${point.y - offset.y}px)`;
    const hit = document.elementFromPoint(point.x, point.y);
    const target = innermostTarget(hit, payload!);
    if (target !== touch.target) {
      touch.target?.onLeave?.();
      touch.target = target;
    }
    target?.onDrag?.(payload!, point);
  };

  const move = (event: PointerEvent) => {
    if (event.pointerId !== down.pointerId) return;
    const point = { x: event.clientX, y: event.clientY };
    touch.point = point;
    if (!payload) {
      const distance = Math.hypot(point.x - start.x, point.y - start.y);
      if (hold) {
        // Moved before the hold completed: it is a scroll, not a drag.
        if (distance > HOLD_SLOP) stop();
        return;
      }
      if (distance < TOUCH_SLOP) return;
      begin();
    }
    event.preventDefault();
    track(point);
  };

  const noMenu = (event: Event) => event.preventDefault();

  const stop = () => {
    window.clearTimeout(timer);
    window.removeEventListener('pointermove', move, true);
    window.removeEventListener('pointerup', up, true);
    window.removeEventListener('pointercancel', cancel, true);
    window.removeEventListener('contextmenu', noMenu, true);
  };

  const finish = (event: PointerEvent, cancelled: boolean) => {
    if (event.pointerId !== down.pointerId) return;
    stop();
    if (!payload) return;
    cancelAnimationFrame(touch.raf);
    const target = touch.target;
    const point = { x: event.clientX, y: event.clientY };
    touch.target = null;
    touch.preview?.remove();
    touch.preview = null;
    touch.active = false;
    options.element.removeAttribute('data-dragging');
    if (target && !cancelled) target.onDrop(payload, point);
    else target?.onLeave?.();
    setSession(null);
    options.onEnd?.(Boolean(target) && !cancelled);
  };
  const up = (e: PointerEvent) => finish(e, false);
  const cancel = (e: PointerEvent) => finish(e, true);
  window.addEventListener('pointermove', move, true);
  window.addEventListener('pointerup', up, true);
  window.addEventListener('pointercancel', cancel, true);
  if (hold) {
    window.addEventListener('contextmenu', noMenu, true);
    timer = window.setTimeout(() => {
      if (!(options.canDrag?.() ?? true)) return stop();
      begin();
      track(touch.point);
    }, LONG_PRESS_MS);
  }
}

function autoscrollAt(point: Point): void {
  const hit = document.elementFromPoint(point.x, point.y);
  const scroller = scrollParent(hit);
  if (!scroller) return;
  const rect = scroller.getBoundingClientRect();
  const speed = (d: number) => Math.ceil(((EDGE - d) / EDGE) * 14);
  if (point.y - rect.top < EDGE) scroller.scrollTop -= speed(point.y - rect.top);
  else if (rect.bottom - point.y < EDGE) scroller.scrollTop += speed(rect.bottom - point.y);
}
